import React, {Fragment, useState, useEffect, useRef} from "react";
import {Col, Input, Row} from "reactstrap";
import dragula from "react-dragula";

const ColumnPicker = ({columnGroups, selectedColumns, onToggle, onSelectAll, onDeselectAll, onReorder}) => {
    const [search, setSearch] = useState('');
    const dragulaContainerRef = useRef(null);
    const drakeRef = useRef(null);
    const onReorderRef = useRef(onReorder);

    useEffect(() => {
        onReorderRef.current = onReorder;
    }, [onReorder]);

    useEffect(() => {
        return () => {
            if (drakeRef.current) {
                drakeRef.current.destroy();
                drakeRef.current = null;
            }
        };
    }, []);

    const initDragula = (node) => {
        if (!node) return;
        dragulaContainerRef.current = node;

        if (drakeRef.current) {
            drakeRef.current.destroy();
            drakeRef.current = null;
        }

        drakeRef.current = dragula([node], {
            direction: 'horizontal',
            moves: function (el, source, handle) {
                return handle.classList.contains('drag-handle') || (handle.closest && handle.closest('.drag-handle'));
            },
        });

        drakeRef.current.on('drop', (el, target, source) => {
            const items = source.querySelectorAll('[data-key]');
            const newOrder = [];
            items.forEach(item => {
                newOrder.push(item.getAttribute('data-key'));
            });
            drakeRef.current.cancel(true);
            onReorderRef.current(newOrder);
        });
    };

    if (!columnGroups || columnGroups.length === 0) return null;

    const allColumns = [];
    columnGroups.forEach(g => {
        g.columns.forEach(c => allColumns.push(c));
    });

    const getLabel = (key) => {
        const col = allColumns.find(c => c.key === key);
        return col ? col.label : key;
    };

    const filteredGroups = columnGroups.map(group => ({
        ...group,
        columns: group.columns.filter(col =>
            col.label.toLowerCase().includes(search.toLowerCase())
        ),
    })).filter(group => group.columns.length > 0);

    return (
        <Fragment>
            <Row className="mb-3">
                <Col>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <label className="mb-0" style={{fontWeight: 600, fontSize: '14px'}}>Columns</label>
                        <div>
                            <a href="#" className="mr-3" style={{fontSize: '12px'}} onClick={(e) => {e.preventDefault(); onSelectAll();}}>Select All</a>
                            <a href="#" style={{fontSize: '12px'}} onClick={(e) => {e.preventDefault(); onDeselectAll();}}>Deselect All</a>
                        </div>
                    </div>
                    <div style={{
                        border: '1px solid #e9ecef',
                        borderRadius: '8px',
                        backgroundColor: '#fff',
                        maxHeight: '320px',
                        overflowY: 'auto',
                    }}>
                        <div style={{padding: '10px 12px', borderBottom: '1px solid #e9ecef', position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1}}>
                            <Input
                                type="text"
                                placeholder="Search fields"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                style={{border: '1px solid #dee2e6', borderRadius: '6px', fontSize: '13px'}}
                            />
                        </div>
                        <div style={{padding: '4px 0'}}>
                            {filteredGroups.map((group, gIdx) => (
                                <div key={gIdx}>
                                    <div style={{
                                        padding: '6px 14px 4px',
                                        fontSize: '11px',
                                        fontWeight: 700,
                                        textTransform: 'uppercase',
                                        color: '#6c757d',
                                        letterSpacing: '0.5px',
                                    }}>
                                        {group.group}
                                    </div>
                                    {group.columns.map((col) => (
                                        <label
                                            key={col.key}
                                            className="d-flex align-items-center mb-0"
                                            style={{
                                                padding: '5px 14px',
                                                cursor: 'pointer',
                                                fontSize: '13px',
                                                transition: 'background-color 0.15s',
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedColumns.includes(col.key)}
                                                onChange={() => onToggle(col.key)}
                                                style={{marginRight: '8px', accentColor: '#005580'}}
                                            />
                                            {col.label}
                                        </label>
                                    ))}
                                </div>
                            ))}
                            {filteredGroups.length === 0 && (
                                <div style={{padding: '12px 14px', color: '#999', fontSize: '13px'}}>No fields match your search.</div>
                            )}
                        </div>
                    </div>
                </Col>
            </Row>

            {selectedColumns.length > 0 && (
                <Row className="mb-3">
                    <Col>
                        <label className="mb-2" style={{fontWeight: 600, fontSize: '14px'}}>
                            Selected Columns
                            <small className="text-muted ml-2" style={{fontWeight: 400}}>Drag to reorder</small>
                        </label>
                        <div
                            ref={initDragula}
                            style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '6px',
                                padding: '10px',
                                border: '1px solid #e9ecef',
                                borderRadius: '8px',
                                backgroundColor: '#fafafa',
                                minHeight: '42px',
                            }}
                        >
                            {selectedColumns.map((key) => (
                                <span
                                    key={key}
                                    data-key={key}
                                    className="d-inline-flex align-items-center"
                                    style={{
                                        backgroundColor: '#e8f4fd',
                                        border: '1px solid #b8daff',
                                        borderRadius: '4px',
                                        padding: '4px 6px 4px 4px',
                                        fontSize: '12px',
                                        lineHeight: '1.4',
                                        userSelect: 'none',
                                    }}
                                >
                                    <span
                                        className="drag-handle"
                                        style={{
                                            cursor: 'grab',
                                            marginRight: '4px',
                                            color: '#999',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                        }}
                                        title="Drag to reorder"
                                    >
                                        <i className="fa fa-ellipsis-v" style={{fontSize: '10px', marginRight: '1px'}}></i>
                                        <i className="fa fa-ellipsis-v" style={{fontSize: '10px'}}></i>
                                    </span>
                                    <span style={{marginRight: '6px'}}>{getLabel(key)}</span>
                                    <span
                                        onClick={() => onToggle(key)}
                                        style={{
                                            cursor: 'pointer',
                                            color: '#999',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            transition: 'color 0.15s',
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.color = '#dc3545'}
                                        onMouseLeave={(e) => e.currentTarget.style.color = '#999'}
                                        title="Remove"
                                    >
                                        <i className="fa fa-times" style={{fontSize: '11px'}}></i>
                                    </span>
                                </span>
                            ))}
                        </div>
                    </Col>
                </Row>
            )}
        </Fragment>
    );
};

export default ColumnPicker;
